import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Button, Field, TileMark, Wordmark } from "@/components/primitives";
import { useStore } from "@/lib/store";
import * as api from "@/lib/api";

/**
 * The reserved host account, mirroring ADMIN_USERNAME in backend config. It is
 * the only name that gets asked for a password, so hosts and players share one
 * door instead of needing to know which page to open.
 */
const HOST_USERNAME = "adminDecore";

const isHostName = (value: string) => value.trim().toLowerCase() === HOST_USERNAME.toLowerCase();

/** Mirrors backend/src/utils/validation.ts exactly. Same rule, same message. */
function validateUsername(value: string): string | null {
  if (value.length < 3) return "Username must be 3 characters.";
  if (value.length > 20) return "Username must not be more than 20 characters.";
  if (!/^[a-zA-Z0-9_]+$/.test(value))
    return "Username must only contain letters, numbers and underscores.";
  return null;
}

function message(error: unknown): string {
  if (error instanceof api.ApiError) return error.message;
  return "Could not reach the server. Is the backend running?";
}

function GateShell({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-[100dvh] flex-col">
      <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-line px-4">
        <Link to="/">
          <TileMark size={20} />
        </Link>
        <Wordmark className="shrink-0 text-[17px] sm:text-[22px]" />
        <Link
          to="/"
          className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink-dim transition-colors hover:text-ink"
        >
          Back
        </Link>
      </header>

      <div className="flex flex-1 items-start justify-center px-5 py-12 sm:items-center sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
          className="w-full max-w-[380px]"
        >
          {children}
        </motion.div>
      </div>
    </main>
  );
}

type Step = "name" | "code" | "hostChoice";

export function Enter() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { identity, setIdentity, connect } = useStore();

  const [step, setStep] = useState<Step>("name");
  const [username, setUsername] = useState(params.get("host") ? HOST_USERNAME : "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const hosting = isHostName(username);

  // A stored token means this browser already has a name. Skip the name step so
  // a refresh mid-game costs nobody their score.
  useEffect(() => {
    if (!identity) return;
    setUsername(identity.username);
    setStep(identity.isAdmin ? "hostChoice" : "code");
  }, [identity]);

  async function openRoom() {
    setBusy(true);
    try {
      const created = await api.createRoom();
      connect(created);
      navigate(`/room/${created}`);
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  }

  async function submitName(e: FormEvent) {
    e.preventDefault();
    const name = username.trim();
    const problem = validateUsername(name);
    if (problem) return setError(problem);
    if (hosting && !password) return setError("Hosts need the room password.");

    setError(null);
    setBusy(true);
    try {
      const who = await api.signIn(name, hosting ? password : undefined);
      setIdentity(who);
      if (who.isAdmin) {
        await openRoom();
        return;
      }
      setStep("code");
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    const value = code.trim().toUpperCase();
    if (value.length < 4) return setError("Room codes are six characters.");
    setError(null);
    setBusy(true);
    try {
      const joined = await api.joinRoom(value);
      connect(joined.roomid);
      navigate(`/room/${joined.roomid}`);
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  }

  function startOver() {
    setIdentity(null);
    setUsername("");
    setPassword("");
    setCode("");
    setError(null);
    setStep("name");
  }

  /* ---------------------------------------------------------------- name */

  if (step === "name") {
    return (
      <GateShell>
        <form onSubmit={submitName} className="flex flex-col gap-7">
          <div>
            <h1 className="wordmark text-[28px]">Pick a name</h1>
            <p className="mt-3 text-[14px] leading-relaxed text-ink-dim">
              Names are unique for as long as the server runs. Choose the one people will shout.
            </p>
          </div>

          <Field
            label="Username"
            value={username}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            placeholder="meher"
            error={hosting ? null : error}
            hint="3 to 20 characters. Letters, numbers and underscores."
            onChange={(e) => {
              setUsername(e.target.value);
              if (error) setError(null);
            }}
          />

          {/* Only the reserved host name is ever asked for a password. */}
          <AnimatePresence initial={false}>
            {hosting && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                className="overflow-hidden"
              >
                <Field
                  label="Host password"
                  type="password"
                  value={password}
                  autoFocus
                  autoComplete="current-password"
                  error={error}
                  hint="That name is reserved for whoever runs the room."
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Button type="submit" disabled={busy}>
            {busy ? "Checking" : hosting ? "Open a room" : "Continue"}
          </Button>
        </form>
      </GateShell>
    );
  }

  /* ----------------------------------------------------- host, signed in */

  if (step === "hostChoice") {
    return (
      <GateShell>
        <div className="flex flex-col gap-7">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-hit">
              Signed in as host
            </p>
            <h1 className="wordmark mt-3 text-[28px]">Open a room</h1>
            <p className="mt-3 text-[14px] leading-relaxed text-ink-dim">
              Hosts run the round and never guess. Start a new room, or rejoin one you have open.
            </p>
          </div>

          <Button disabled={busy} onClick={openRoom}>
            {busy ? "Opening" : "New room"}
          </Button>

          <form onSubmit={submitCode} className="flex flex-col gap-4 border-t border-line pt-7">
            <Field
              label="Or rejoin by code"
              value={code}
              autoComplete="off"
              spellCheck={false}
              maxLength={6}
              placeholder="K3F9DM"
              error={error}
              className="wordmark h-14 text-[20px] tracking-[0.3em]"
              onChange={(e) => {
                setCode(e.target.value.replace(/\s/g, "").toUpperCase());
                if (error) setError(null);
              }}
            />
            <Button type="submit" variant="ghost" disabled={busy || code.length < 4}>
              Rejoin
            </Button>
          </form>

          <button
            type="button"
            onClick={startOver}
            className="self-start text-[12px] font-bold uppercase tracking-[0.1em] text-ink-faint transition-colors hover:text-ink"
          >
            Sign in as someone else
          </button>
        </div>
      </GateShell>
    );
  }

  /* ---------------------------------------------------------------- code */

  return (
    <GateShell>
      <form onSubmit={submitCode} className="flex flex-col gap-7">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-hit">
            Signed in as {username}
          </p>
          <h1 className="wordmark mt-3 text-[28px]">Room code</h1>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-dim">
            The host has it on screen. Codes are per room and die with the room.
          </p>
        </div>

        <Field
          label="Code"
          value={code}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          maxLength={6}
          placeholder="K3F9DM"
          error={error}
          className="wordmark h-16 text-[22px] tracking-[0.3em]"
          onChange={(e) => {
            setCode(e.target.value.replace(/\s/g, "").toUpperCase());
            if (error) setError(null);
          }}
        />

        <div className="flex gap-3">
          <Button type="button" variant="ghost" onClick={startOver}>
            Change name
          </Button>
          <Button type="submit" className="flex-1" disabled={busy}>
            {busy ? "Connecting" : "Enter room"}
          </Button>
        </div>
      </form>
    </GateShell>
  );
}
